import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Save, Users, Layout, Search, Eye, Megaphone, Send, Workflow, MessageCircle, FileText, AlertCircle, UserCircle, BarChart3 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
    getCollaboratorAccess,
    setCollaboratorAccess,
} from '@/services/api/collaborators';
import type {
    Employee,
    MetaPage,
    CollaboratorPermission,
    CollaboratorAccessRecord,
    CollaboratorAccessUpdate,
} from '../types/meta.types';

const ALL_PERMS: { key: CollaboratorPermission; label: string; description: string; icon: any }[] = [
    { key: 'view', label: 'Ver', description: 'Ver na listagem', icon: Eye },
    { key: 'broadcast_create', label: 'Criar broadcast', description: 'Criar broadcasts (sem disparar)', icon: Megaphone },
    { key: 'broadcast_send', label: 'Disparar broadcast', description: 'Disparar broadcasts criados', icon: Send },
    { key: 'flow_edit', label: 'Editar flows', description: 'Criar e editar fluxos', icon: Workflow },
    { key: 'auto_reply_edit', label: 'Auto-replies', description: 'Criar e editar auto-replies', icon: MessageCircle },
    { key: 'lead_view', label: 'Ver leads', description: 'Visualizar leads desta entidade', icon: FileText },
    { key: 'dashboard_view', label: 'Ver dashboard', description: 'Acessar relatórios da messageria', icon: BarChart3 },
    { key: 'revoke_account', label: 'Revogar conta', description: 'Desconectar oauth_account (perigoso)', icon: AlertCircle },
];

interface CollaboratorAccessEditorProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    collaborator: Employee | null;
    pages: MetaPage[];
}

/** Estado interno: por (resource_type:resource_key) → Set de permissões */
type AccessState = Map<string, Set<CollaboratorPermission>>;

const accessKey = (type: string, key: string) => `${type}:${key}`;

export default function CollaboratorAccessEditor({
    open,
    onOpenChange,
    collaborator,
    pages,
}: CollaboratorAccessEditorProps) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [accessMap, setAccessMap] = useState<AccessState>(new Map());
    const [activeTab, setActiveTab] = useState<'meta_user' | 'page'>('meta_user');
    const [searchTerm, setSearchTerm] = useState('');

    // Carrega acessos existentes do colaborador
    const { data: existingAccess } = useQuery({
        queryKey: ['collaborator-access', collaborator?.id],
        queryFn: () => collaborator ? getCollaboratorAccess(collaborator.id) : Promise.resolve([]),
        enabled: !!collaborator && open,
    });

    // Inicializa estado a partir dos acessos existentes.
    // Depende de `data` direto (não do fallback `?? []`) pra não criar nova
    // referência a cada render — sem isso o useEffect entrava em loop quando
    // data ainda era undefined.
    useEffect(() => {
        const list = (existingAccess as CollaboratorAccessRecord[] | undefined) ?? [];
        const m: AccessState = new Map();
        list.forEach((a) => {
            m.set(accessKey(a.resource_type, a.resource_key), new Set(a.permissions));
        });
        setAccessMap(m);
    }, [existingAccess]);

    // Agrupa páginas por meta_user_name (grupo "user mestre" virtual)
    const metaUserGroups = useMemo(() => {
        const map = new Map<string, MetaPage[]>();
        pages.forEach(p => {
            const name = p.meta_user_name || '(sem nome)';
            if (!map.has(name)) map.set(name, []);
            map.get(name)!.push(p);
        });
        return Array.from(map.entries())
            .map(([name, pgs]) => ({ name, pages: pgs }))
            .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
    }, [pages]);

    // Lista filtrada conforme aba ativa
    const filteredMetaUsers = useMemo(() => {
        if (!searchTerm) return metaUserGroups;
        const q = searchTerm.toLowerCase();
        return metaUserGroups.filter(g => g.name.toLowerCase().includes(q));
    }, [metaUserGroups, searchTerm]);

    const filteredPages = useMemo(() => {
        if (!searchTerm) return pages;
        const q = searchTerm.toLowerCase();
        return pages.filter(p =>
            p.page_name.toLowerCase().includes(q) || p.page_id.includes(searchTerm.trim())
        );
    }, [pages, searchTerm]);

    const togglePerm = (rtype: string, rkey: string, perm: CollaboratorPermission) => {
        setAccessMap(prev => {
            const next = new Map(prev);
            const k = accessKey(rtype, rkey);
            const cur = new Set(next.get(k) || []);
            if (cur.has(perm)) cur.delete(perm);
            else {
                cur.add(perm);
                // view é implícita quando qualquer outra está marcada
                cur.add('view');
            }
            if (cur.size === 0) next.delete(k);
            else next.set(k, cur);
            return next;
        });
    };

    const removeResource = (rtype: string, rkey: string) => {
        setAccessMap(prev => {
            const next = new Map(prev);
            next.delete(accessKey(rtype, rkey));
            return next;
        });
    };

    const grantViewOnly = (rtype: string, rkey: string) => {
        setAccessMap(prev => {
            const next = new Map(prev);
            next.set(accessKey(rtype, rkey), new Set<CollaboratorPermission>(['view']));
            return next;
        });
    };

    const saveMutation = useMutation({
        mutationFn: async () => {
            if (!collaborator) throw new Error('Sem colaborador');
            const accesses: CollaboratorAccessUpdate[] = Array.from(accessMap.entries()).map(([k, perms]) => {
                const [resource_type, resource_key] = k.split(':');
                return {
                    resource_type: resource_type as 'meta_user' | 'page',
                    resource_key,
                    permissions: Array.from(perms),
                };
            });
            return setCollaboratorAccess(collaborator.id, accesses);
        },
        onSuccess: (r) => {
            toast({ title: 'Acessos salvos', description: `${r.saved} acesso(s) atualizado(s).` });
            queryClient.invalidateQueries({ queryKey: ['collaborator-access', collaborator?.id] });
            onOpenChange(false);
        },
        onError: (e: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar',
                description: e?.response?.data?.detail || 'Não foi possível salvar os acessos.',
            });
        },
    });

    const totalGrants = accessMap.size;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[800px] max-h-[90vh] flex flex-col p-0 gap-0">
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
                    <DialogTitle className="text-2xl font-bold text-[#0f172a]">
                        Acessos de {collaborator?.name || ''}
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                        Define quais contas Meta e/ou páginas individuais este colaborador pode
                        acessar, e o que ele pode fazer.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-hidden flex flex-col">
                    {/* Tabs + busca */}
                    <div className="border-b border-gray-100 px-6 pt-4 shrink-0">
                        <div className="flex items-center gap-1 -mb-px">
                            {([
                                { id: 'meta_user' as const, label: 'Por Conta Meta', icon: UserCircle, count: metaUserGroups.length },
                                { id: 'page' as const, label: 'Por Página', icon: Layout, count: pages.length },
                            ]).map(t => (
                                <button
                                    key={t.id}
                                    onClick={() => setActiveTab(t.id)}
                                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${
                                        activeTab === t.id
                                            ? 'border-indigo-600 text-indigo-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-900'
                                    }`}
                                >
                                    <t.icon className="h-4 w-4" />
                                    {t.label}
                                    <Badge variant="outline" className="ml-1 text-[10px] py-0 h-4">{t.count}</Badge>
                                </button>
                            ))}
                        </div>

                        <div className="mt-3 pb-3 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                                placeholder={activeTab === 'meta_user' ? 'Buscar conta Meta...' : 'Buscar página...'}
                                className="pl-9"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Lista de recursos com permissões */}
                    <div className="flex-1 overflow-y-auto px-6 py-4">
                        {totalGrants > 0 && (
                            <Alert className="mb-4 border-indigo-200 bg-indigo-50/40">
                                <Users className="h-4 w-4 text-indigo-600" />
                                <AlertDescription className="text-sm text-indigo-900">
                                    Total: <strong>{totalGrants}</strong> acesso{totalGrants !== 1 ? 's' : ''} configurado{totalGrants !== 1 ? 's' : ''}.
                                </AlertDescription>
                            </Alert>
                        )}

                        {activeTab === 'meta_user' ? (
                            <div className="space-y-2">
                                {filteredMetaUsers.length === 0 && (
                                    <p className="text-sm text-gray-500 text-center py-8">
                                        Nenhuma conta encontrada.
                                    </p>
                                )}
                                {filteredMetaUsers.map(group => (
                                    <ResourceRow
                                        key={`mu:${group.name}`}
                                        title={group.name}
                                        subtitle={`${group.pages.length} página${group.pages.length !== 1 ? 's' : ''}`}
                                        icon={UserCircle}
                                        iconColor="text-violet-600 bg-violet-50"
                                        perms={accessMap.get(accessKey('meta_user', group.name))}
                                        onToggle={(perm) => togglePerm('meta_user', group.name, perm)}
                                        onGrantViewOnly={() => grantViewOnly('meta_user', group.name)}
                                        onRemove={() => removeResource('meta_user', group.name)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {filteredPages.length === 0 && (
                                    <p className="text-sm text-gray-500 text-center py-8">
                                        Nenhuma página encontrada.
                                    </p>
                                )}
                                {filteredPages.map(p => (
                                    <ResourceRow
                                        key={`p:${p.id}`}
                                        title={p.page_name}
                                        subtitle={`ID: ${p.page_id}${p.meta_user_name ? ` · ${p.meta_user_name}` : ''}`}
                                        icon={Layout}
                                        iconColor="text-blue-600 bg-blue-50"
                                        perms={accessMap.get(accessKey('page', String(p.id)))}
                                        onToggle={(perm) => togglePerm('page', String(p.id), perm)}
                                        onGrantViewOnly={() => grantViewOnly('page', String(p.id))}
                                        onRemove={() => removeResource('page', String(p.id))}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0 px-6 py-4 border-t border-gray-100 shrink-0 bg-gray-50/30">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={saveMutation.isPending}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => saveMutation.mutate()}
                        disabled={saveMutation.isPending}
                        className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                    >
                        <Save className="mr-2 h-4 w-4" />
                        {saveMutation.isPending ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

interface ResourceRowProps {
    title: string;
    subtitle: string;
    icon: any;
    iconColor: string;
    perms: Set<CollaboratorPermission> | undefined;
    onToggle: (perm: CollaboratorPermission) => void;
    onGrantViewOnly: () => void;
    onRemove: () => void;
}

function ResourceRow({ title, subtitle, icon: Icon, iconColor, perms, onToggle, onGrantViewOnly, onRemove }: ResourceRowProps) {
    const isGranted = !!perms && perms.size > 0;
    const [expanded, setExpanded] = useState(isGranted);

    return (
        <div className={`rounded-lg border ${isGranted ? 'border-indigo-200 bg-indigo-50/20' : 'border-gray-200 bg-white'}`}>
            <div className="px-3 py-2.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${iconColor}`}>
                        <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
                        <p className="text-xs text-gray-500 truncate">{subtitle}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {isGranted && (
                        <Badge variant="outline" className="text-[10px] py-0 h-5 border-indigo-200 bg-indigo-50/50 text-indigo-700">
                            {perms!.size} permissão{perms!.size !== 1 ? 'ões' : ''}
                        </Badge>
                    )}
                    {!isGranted ? (
                        <Button size="sm" variant="outline" onClick={() => { onGrantViewOnly(); setExpanded(true); }}>
                            Conceder acesso
                        </Button>
                    ) : (
                        <>
                            <Button size="sm" variant="ghost" onClick={() => setExpanded(e => !e)}>
                                {expanded ? 'Recolher' : 'Editar'}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={onRemove} className="text-red-600 hover:bg-red-50">
                                Remover
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {expanded && isGranted && (
                <div className="px-3 pb-3 pt-1 border-t border-indigo-100/50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {ALL_PERMS.map(p => {
                            const checked = perms!.has(p.key);
                            const Icon2 = p.icon;
                            return (
                                <label
                                    key={p.key}
                                    className={`flex items-start gap-2.5 p-2 rounded cursor-pointer hover:bg-white transition-colors ${
                                        checked ? 'bg-white' : ''
                                    }`}
                                >
                                    <Checkbox
                                        checked={checked}
                                        onCheckedChange={() => onToggle(p.key)}
                                        disabled={p.key === 'view' && checked && perms!.size === 1}
                                        className="mt-0.5"
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <Icon2 className="h-3.5 w-3.5 text-gray-500" />
                                            <span className="text-xs font-medium text-gray-900">{p.label}</span>
                                        </div>
                                        <p className="text-[11px] text-gray-500 leading-snug">{p.description}</p>
                                    </div>
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
